// components/project/QuantityCorrectionDialog.jsx
// STEP 5C · Dialog UNICO di rettifica contrattuale della quantità corrente di una voce SAL.
// Bidirezionale: il BACKEND è authoritative sulla direzione.
//   proposta > corrente → path positivo 5C (rettifica-quantita)
//   proposta < corrente → path negativo certificato 5B-2b (stralcio-quantita)
//   proposta == corrente → NO_OP (nessuna mutazione)
// React NON calcola economics: usa esclusivamente l'anteprima del backend.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { salApi } from '../../lib/api';
import { toast } from 'sonner';
import { SlidersHorizontal, TrendingUp, TrendingDown, AlertTriangle, Loader2, Info } from 'lucide-react';

const eur = (v) => `€ ${(Number(v) || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (v) => (Number(v) || 0).toString().replace('.', ',');
const uuid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `op-${Date.now()}-${Math.random().toString(16).slice(2)}`);

const DIM_KEYS = ['parts', 'length', 'width', 'height_weight', 'manual_quantity'];

export const QuantityCorrectionDialog = ({ open, onOpenChange, item, measurementRowId = null, onDone }) => {
  const isRow = measurementRowId != null;
  const row = isRow ? (item.sal_measurement_lines || []).find(r => r.source_measurement_id === measurementRowId) : null;
  const currentQty = item.current_quantity ?? item.original_quantity ?? item.total_quantity ?? 0;

  const [newQty, setNewQty] = useState(String(currentQty));
  const [dims, setDims] = useState({});
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState(null);   // {direction, before, after, delta, unitPrice, netImpact, completionAfter, unit, quantity_to_remove?}
  const [previewError, setPreviewError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const opKeyRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    opKeyRef.current = uuid();
    setReason(''); setPreview(null); setPreviewError(null); setSubmitting(false);
    setNewQty(String(currentQty));
    if (isRow && row) {
      const init = {};
      DIM_KEYS.forEach(k => {
        const cur = row[`current_${k}`];
        const val = cur != null ? cur : row[k];
        if (val != null && val !== '') init[k] = String(val);
      });
      setDims(init);
    } else {
      setDims({});
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const buildPositivePayload = useCallback((reasonText) => {
    if (isRow) {
      const proposed_dimensions = {};
      DIM_KEYS.forEach(k => { if (dims[k] != null && dims[k] !== '') proposed_dimensions[k] = dims[k]; });
      return { measurement_row_id: measurementRowId, proposed_dimensions, reason: reasonText };
    }
    return { proposed_current_quantity: parseFloat(String(newQty).replace(',', '.')), reason: reasonText };
  }, [isRow, dims, measurementRowId, newQty]);

  const fetchPreview = useCallback(async () => {
    const reasonStub = reason && reason.trim().length >= 6 ? reason.trim() : 'anteprima__________';
    if (!isRow) {
      const q = parseFloat(String(newQty).replace(',', '.'));
      if (isNaN(q) || q < 0) { setPreview(null); setPreviewError(null); return; }
    }
    setLoading(true);
    try {
      const res = await salApi.previewQuantityIncrease(item.id, buildPositivePayload(reasonStub));
      const d = res.data;
      setPreview({
        direction: 'positive', before: d.current_quantity_before, after: d.proposed_current_quantity,
        executed: d.executed_quantity, delta: d.quantity_delta, unitPrice: d.contract_unit_price_net,
        netImpact: d.net_delta, completionAfter: d.completion_percentage_after, unit: d.unit,
        rowBefore: d.row_current_before, rowAfter: d.row_current_after,
        feasibility: d.economic_feasibility,
      });
      setPreviewError(null);
    } catch (e) {
      const detail = e?.response?.data?.detail || {};
      if (detail.code === 'NO_OP') {
        setPreview({ direction: 'none' }); setPreviewError(null);
      } else if (detail.code === 'USE_NEGATIVE_PATH') {
        // Direzione NEGATIVA (authoritative dal backend) → anteprima 5B-2b certificata
        try {
          const neg = await salApi.previewQuantityStralcio(item.id, {
            quantity_to_remove: detail.quantity_to_remove,
            reason: reasonStub, measurement_row_id: measurementRowId,
          });
          const nd = neg.data;
          setPreview({
            direction: 'negative', before: nd.current_quantity_before, after: nd.current_quantity_after,
            executed: nd.executed_quantity, delta: -Math.abs(detail.quantity_delta), unitPrice: nd.contract_unit_price_net,
            netImpact: -Math.abs(nd.net_reduction), completionAfter: nd.completion_percentage_after, unit: nd.unit,
            rowBefore: detail.row_current_before, rowAfter: detail.row_current_after,
            quantity_to_remove: detail.quantity_to_remove,
          });
          setPreviewError(null);
        } catch (e2) {
          setPreview(null);
          setPreviewError(e2?.response?.data?.detail?.message || 'Anteprima riduzione non disponibile');
        }
      } else {
        setPreview(null);
        setPreviewError(detail.message || 'Anteprima non disponibile');
      }
    } finally {
      setLoading(false);
    }
  }, [isRow, newQty, dims, item.id, measurementRowId, reason, buildPositivePayload]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(fetchPreview, 300);
    return () => clearTimeout(t);
  }, [open, newQty, dims, fetchPreview]);

  const hasChange = preview && (preview.direction === 'positive' || preview.direction === 'negative');
  const canConfirm = hasChange && !previewError && reason.trim().length >= 6 && !submitting;

  const confirm = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    try {
      let res;
      if (preview.direction === 'positive') {
        res = await salApi.applyQuantityIncrease(item.id, {
          ...buildPositivePayload(reason.trim()), idempotency_key: opKeyRef.current,
        });
      } else {
        res = await salApi.applyQuantityStralcio(item.id, {
          quantity_to_remove: preview.quantity_to_remove, reason: reason.trim(),
          measurement_row_id: measurementRowId, idempotency_key: opKeyRef.current,
        });
      }
      const outcome = res.data?.outcome;
      if (outcome === 'manual_review') {
        toast.warning('Rettifica in gestione amministrativa: non ci sono rate future sufficienti per assorbirla automaticamente. La quantità NON è stata modificata.');
      } else {
        toast.success(preview.direction === 'positive'
          ? 'Aumento quantità applicato: variazione contrattuale registrata.'
          : 'Riduzione quantità applicata: variazione contrattuale registrata.');
      }
      onOpenChange(false);
      onDone && onDone();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Errore durante la rettifica quantità');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const positive = preview?.direction === 'positive';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="quantity-correction-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#3C3C3C]">
            <SlidersHorizontal className="w-4 h-4 text-[#FF7819]" /> Rettifica quantità
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="text-xs text-[#737373]">
            {item.code} · {item.description}
            {isRow ? <span className="ml-1 text-[#FF7819]">(riga misura{row?.description ? `: ${row.description}` : ''})</span> : null}
          </div>

          {/* Riepilogo base voce */}
          <div className="grid grid-cols-3 gap-2 text-xs">
            <Cell label="Originaria" value={`${num(item.original_quantity ?? item.total_quantity)} ${item.unit || ''}`} testid="corr-original" />
            <Cell label="Corrente" value={`${num(currentQty)} ${item.unit || ''}`} testid="corr-current" />
            <Cell label="Eseguita" value={`${num(item.completed_quantity)} ${item.unit || ''}`} testid="corr-executed" />
          </div>

          {/* INPUT: semplice = nuova quantità; misura = dimensioni proposte */}
          {!isRow ? (
            <div>
              <Label className="text-xs">Nuova quantità corrente</Label>
              <Input
                type="text" inputMode="decimal" value={newQty}
                onChange={(e) => setNewQty(e.target.value)}
                className="h-9 input-abitalo mt-1"
                data-testid="corr-new-qty-input"
              />
            </div>
          ) : (
            <div className="space-y-2">
              {row && (
                <div className="text-[11px] text-[#a3a3a3]" data-testid="corr-original-dims">
                  Misure originali (sola lettura): {[row.parts, row.length, row.width, row.height_weight].filter(v => v != null && v !== '').map(num).join(' × ') || '—'}
                </div>
              )}
              <Label className="text-xs">Misure proposte</Label>
              <div className="grid grid-cols-2 gap-2">
                {DIM_KEYS.map(k => (
                  <div key={k}>
                    <div className="text-[10px] text-[#a3a3a3] uppercase">{({ parts: 'Parti', length: 'Lungh.', width: 'Largh.', height_weight: 'Alt./Peso', manual_quantity: 'Q.tà manuale' })[k]}</div>
                    <Input
                      type="text" inputMode="decimal" value={dims[k] ?? ''}
                      onChange={(e) => setDims(prev => ({ ...prev, [k]: e.target.value }))}
                      className="h-8 input-abitalo text-sm"
                      data-testid={`corr-dim-${k}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {loading && <div className="flex items-center gap-2 text-xs text-[#737373]"><Loader2 className="w-3 h-3 animate-spin" /> Calcolo impatto…</div>}

          {previewError && !loading && (
            <div className="flex items-start gap-2 text-xs text-red-600 bg-red-50 rounded p-2" data-testid="corr-preview-error">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /> {previewError}
            </div>
          )}

          {preview?.direction === 'none' && !loading && (
            <div className="flex items-start gap-2 text-xs text-[#737373] bg-[#fafafa] rounded p-2" data-testid="corr-noop">
              <Info className="w-3.5 h-3.5 mt-0.5" /> La quantità proposta è uguale a quella corrente: nessuna modifica.
            </div>
          )}

          {hasChange && !loading && (
            <div className={`rounded-md border p-3 space-y-1 ${positive ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/40'}`} data-testid="corr-preview">
              <div className={`flex items-center gap-1 text-xs font-semibold ${positive ? 'text-emerald-700' : 'text-red-600'}`} data-testid="corr-direction">
                {positive ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                {positive ? 'Aumento contrattuale' : 'Riduzione contrattuale'}
              </div>
              {isRow && (
                <Row label="Q.tà riga (prima → dopo)" value={`${num(preview.rowBefore)} → ${num(preview.rowAfter)} ${preview.unit || ''}`} />
              )}
              <Row label="Corrente" value={`${num(preview.before)} ${preview.unit || ''}`} />
              <Row label="Nuova" value={`${num(preview.after)} ${preview.unit || ''}`} highlight />
              <Row label="Delta" value={`${preview.delta > 0 ? '+' : ''}${num(preview.delta)} ${preview.unit || ''}`} highlight />
              <Row label="Prezzo unitario netto" value={eur(preview.unitPrice)} />
              <div className="border-t border-[#e5e5e5] my-1" />
              <Row label="Impatto contratto" value={`${preview.netImpact > 0 ? '+ ' : '- '}${eur(Math.abs(preview.netImpact))}`} strong strongClass={positive ? 'text-emerald-700' : 'text-red-600'} testid="corr-net-impact" />
              <Row label="Avanzamento dopo" value={`${num(preview.completionAfter)}%`} />
              <div className={`mt-1 text-[11px] ${positive ? 'text-emerald-700' : 'text-red-600'}`} data-testid="corr-business-note">
                {positive
                  ? "L'aumento entrerà nel piano SAL principale e aggiornerà le rate future non ancora maturate."
                  : 'La riduzione aggiornerà il valore contrattuale e le rate future non ancora maturate.'}
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs">Motivazione (obbligatoria)</Label>
            <Textarea
              value={reason} onChange={(e) => setReason(e.target.value)}
              rows={2} className="mt-1 input-abitalo text-sm"
              placeholder="Es. Maggiore quantità rilevata in cantiere sulla parete ospiti"
              data-testid="corr-reason-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="corr-cancel-btn">Annulla</Button>
          <Button
            className={positive ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-[#FF7819] hover:bg-[#e56a13]'}
            disabled={!canConfirm}
            onClick={confirm}
            data-testid="corr-confirm-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <SlidersHorizontal className="w-4 h-4 mr-1" />}
            Conferma rettifica
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Cell = ({ label, value, testid }) => (
  <div className="bg-white rounded border border-[#eee] p-1.5">
    <div className="text-[#a3a3a3]">{label}</div>
    <div className="font-semibold text-[#3C3C3C]" data-testid={testid}>{value}</div>
  </div>
);

const Row = ({ label, value, highlight, strong, strongClass, testid }) => (
  <div className="flex items-center justify-between text-xs">
    <span className="text-[#737373]">{label}</span>
    <span
      className={`${strong ? `font-bold ${strongClass || 'text-[#3C3C3C]'}` : highlight ? 'font-semibold text-[#FF7819]' : 'text-[#3C3C3C]'}`}
      data-testid={testid}
    >{value}</span>
  </div>
);

export default QuantityCorrectionDialog;
